import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { Loader2, CircleCheck, CircleX, User, AtSign, Mail, Calendar, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { useToast } from '@/components/ui/Toast';
import { GET_PROFILE_FOR_EDIT, CHECK_USERNAME_AVAILABILITY } from '@/graphql/queries';
import { UPDATE_PROFILE } from '@/graphql/mutations';
import { useAuth } from '@/context/AuthContext';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { Gender } from '@/gql/graphql';

const GENDER_OPTIONS = [
    { value: Gender.Male, labelKey: 'edit.genderMale' },
    { value: Gender.Female, labelKey: 'edit.genderFemale' },
    { value: Gender.Other, labelKey: 'edit.genderOther' },
] as const;

export function EditProfilePage() {
    const navigate = useNavigate();
    const { t } = useTranslation('profile');
    const { user: authUser } = useAuth();
    const { data, loading } = useQuery(GET_PROFILE_FOR_EDIT);
    const [updateUser, { loading: saving }] = useMutation(UPDATE_PROFILE);
    const { toast } = useToast();

    const me = data?.me;
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [gender, setGender] = useState<Gender | ''>('');
    const [birthDate, setBirthDate] = useState('');
    const [initialized, setInitialized] = useState(false);

    if (me && !initialized) {
        setUsername(me.username || '');
        setEmail(me.email || '');
        setFirstName(me.firstName || '');
        setLastName(me.lastName || '');
        setGender(me.gender || '');
        if (me.birthDate) {
            setBirthDate(new Date(me.birthDate).toISOString().split('T')[0]);
        }
        setInitialized(true);
    }

    const [debouncedUsername] = useDebounce(username, 500);
    const originalUsername = data?.me?.username || '';
    const usernameChanged = debouncedUsername.toLowerCase() !== originalUsername.toLowerCase();

    const { data: usernameCheck, loading: checkingUsername } = useQuery(
        CHECK_USERNAME_AVAILABILITY,
        {
            variables: { username: debouncedUsername },
            skip: debouncedUsername.length < 3 || !usernameChanged,
        },
    );
    const usernameAvailable = !usernameChanged || usernameCheck?.checkUsernameAvailability;

    const handleSave = async (e?: React.FormEvent) => {
        e?.preventDefault();
        try {
            const input: Record<string, string> = { username, email, firstName, lastName };
            if (gender) input.gender = gender;
            if (birthDate) {
                const dateObj = new Date(birthDate);
                input.birthDate = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 12, 0, 0)).toISOString();
            }
            await updateUser({ variables: { input } });
            navigate('/profile');
        } catch (err) {
            console.error(err);
            toast(t('edit.error'), 'error');
        }
    };

    if (loading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-emerald-600"/></div>;

    const inputClass = "w-full h-12 px-4 bg-slate-50/80 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium text-slate-900";

    return (
        <Page>
            <PageHeader
                title={t('edit.title')}
                backLink="/profile"
                width="narrow"
            />

            <PageContent width="narrow" className="space-y-5 pb-24">
                {/* Hero gradient */}
                <div className="animate-section animate-section-1">
                    <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-600 to-emerald-700 rounded-3xl px-6 pt-8 pb-10 overflow-hidden">
                        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10" />
                        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10" />

                        <div className="relative flex flex-col items-center">
                            <UserAvatar
                                src={authUser?.pictureUrl}
                                alt={authUser?.username}
                                size="lg"
                                className="border-4 border-white/90 shadow-xl"
                            />
                            <h2 className="mt-3 text-lg font-bold text-white">{authUser?.username}</h2>
                            <p className="text-emerald-200 text-sm">{authUser?.email}</p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSave} className="space-y-5">
                    {/* Section: Identity */}
                    <div className="animate-section animate-section-2 space-y-3">
                        <SectionHeader icon={<User size={14} />} label={t('edit.sectionIdentity')} />
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 ml-1">{t('edit.firstName')}</label>
                                    <input
                                        className={inputClass}
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 ml-1">{t('edit.lastName')}</label>
                                    <input
                                        className={inputClass}
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section: Account */}
                    <div className="animate-section animate-section-3 space-y-3">
                        <SectionHeader icon={<AtSign size={14} />} label={t('edit.sectionAccount')} />
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
                            {/* Username */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-slate-500 ml-1">{t('edit.username')}</label>
                                <input
                                    className={inputClass}
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                                {usernameChanged && debouncedUsername.length >= 3 && (
                                    <div className="flex items-center gap-1.5 mt-1 ml-1">
                                        {checkingUsername ? (
                                            <span className="text-xs text-slate-400">{t('edit.usernameChecking')}</span>
                                        ) : usernameAvailable ? (
                                            <>
                                                <CircleCheck size={14} className="text-emerald-500" />
                                                <span className="text-xs text-emerald-600 font-medium">{t('edit.usernameAvailable')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <CircleX size={14} className="text-red-500" />
                                                <span className="text-xs text-red-600 font-medium">{t('edit.usernameTaken')}</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Email */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-slate-500 ml-1">{t('edit.email')}</label>
                                <div className="relative">
                                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="email"
                                        className={cn(inputClass, "pl-10")}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section: Personal */}
                    <div className="animate-section animate-section-4 space-y-3">
                        <SectionHeader icon={<Calendar size={14} />} label={t('edit.sectionPersonal')} />
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
                            {/* Birth date */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-slate-500 ml-1">{t('edit.birthDate')}</label>
                                <input
                                    type="date"
                                    className={inputClass}
                                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                    value={birthDate}
                                    onChange={(e) => setBirthDate(e.target.value)}
                                />
                            </div>

                            {/* Gender toggle */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-slate-500 ml-1">{t('edit.gender')}</label>
                                <div className="flex gap-2">
                                    {GENDER_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setGender(opt.value)}
                                            className={cn(
                                                'flex-1 h-12 rounded-xl text-sm font-bold transition-all',
                                                gender === opt.value
                                                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                                                    : 'bg-slate-50/80 border border-slate-200 text-slate-500 hover:border-slate-300'
                                            )}
                                        >
                                            {t(opt.labelKey)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Save button */}
                    <div className="animate-section animate-section-5 pt-2">
                        <button
                            type="submit"
                            disabled={saving || (usernameChanged && !usernameAvailable)}
                            className={cn(
                                "w-full h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-2.5 transition-all active:scale-95",
                                saving || (usernameChanged && !usernameAvailable)
                                    ? "bg-slate-100 text-slate-400 cursor-not-allowed shadow-none"
                                    : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-500/25"
                            )}
                        >
                            {saving ? (
                                <Loader2 size={22} className="animate-spin" />
                            ) : (
                                <>
                                    <Save size={20} />
                                    {t('edit.save')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </PageContent>
        </Page>
    );
}

function SectionHeader({ icon, label }: { icon: React.ReactNode; label: string }) {
    return (
        <div className="flex items-center gap-2 pl-1">
            <div className="w-6 h-6 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                {icon}
            </div>
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">{label}</h3>
        </div>
    );
}
